import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import Switch from '../../../../../Switch';
import Badges from './Badges';
import classes from './styles.module.scss';
import Checkbox from '../../../../../Form/Checkbox';
import TextInput from '../../../../../Form/TextInput';
import Textarea from '../../../../../Form/Textarea';
import SelectedUsers from '../../../../SelectedUsers';

export default function Settings({ company, refetchCompany }) {
  const { t } = useTranslation();

  // Notification table state
  const [notificationSettings, setNotificationSettings] = useState([
    { key: 'newApplicants', active: true, email: false, inApp: false },
    { key: 'assessment', active: true, email: false, inApp: false },
    { key: 'interview', active: true, email: false, inApp: false },
    { key: 'offer', active: true, email: false, inApp: false },
    { key: 'onboarding', active: true, email: false, inApp: false },
  ]);
  const toggleNotification = (rowKey, field) => {
    setNotificationSettings((rows) =>
      rows.map((row) =>
        row.key === rowKey ? { ...row, [field]: !row[field] } : row
      )
    );
  };

  return (
    <div className={classes.Settings}>
      {/* Main two-column content */}
      <div className={classes.Main}>
        <div className={classes.Left}>
          <Badges company={company} refetch={refetchCompany} />

          {/* Company Profile Section */}
          <div className={classes.Card}>
            <div className={classes.SectionTitle}>
              {t(
                'dashboardComponents.Companies.CompanyDetails.Settings.CompanyProfile.title',
                'Company Profile'
              )}
            </div>

            <div className={classes.ProfileFields}>
              <TextInput
                name="companyName"
                value={company?.company?.name || ''}
                readonly
                height={50}
                placeholder={t(
                  'dashboardComponents.Companies.CompanyDetails.Settings.CompanyProfile.companyNamePlaceholder',
                  'Company Name'
                )}
              />

              <TextInput
                name="industryLocation"
                value={
                  [
                    company?.company?.industry?.name,
                    company?.company?.locations?.[0]?.addressCity,
                  ]
                    .filter(Boolean)
                    .join(' / ') || ''
                }
                readonly
                height={50}
                placeholder={t(
                  'dashboardComponents.Companies.CompanyDetails.Settings.CompanyProfile.industryLocationPlaceholder',
                  'Industry / Location'
                )}
              />

              <Textarea
                name="companyJoiningNote"
                value={company?.company?.companyJoiningNote || ''}
                readonly
                height={100}
                placeholder={t(
                  'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Company Joining Note here',
                  'Why join us?'
                )}
              />

              <Textarea
                name="companyDescription"
                value={company?.company?.companyDescription || ''}
                readonly
                height={100}
                placeholder={t(
                  'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Company Description here',
                  'About company'
                )}
              />
            </div>
          </div>
        </div>
        <div className={classes.Right}>
          <div className={classes.Managers}>
            <div className={classes.SectionTitle}>
              {t('company.settings.managers.title', 'Managers')}
            </div>
            {company?.company?.managers?.length > 0 ? (
              <SelectedUsers
                readOnly
                invitedUsers={company?.company?.managers?.map((manager) => ({
                  value: manager.id,
                  label: `${manager.firstName} ${manager.lastName}`,
                  originalData: manager,
                }))}
                withFormik
                isExternalUserList
                setInvitedUsers={() => {}}
              />
            ) : (
              <div className={classes.EmptyMessage}>
                {t(
                  'dashboardComponents.Companies.CompanyDetails.Settings.Managers.emptyMessage',
                  'No managers added'
                )}
              </div>
            )}
          </div>
          <div className={classes.Managers}>
            <div className={classes.SectionTitle}>
              {t(
                'company.settings.managersAccess.title',
                'Managers with access'
              )}
            </div>
            {company?.company?.managers?.filter(
              (manager) => manager.userId !== null
            )?.length > 0 ? (
              <SelectedUsers
                readOnly
                invitedUsers={company?.company?.managers
                  ?.filter((manager) => manager.userId !== null)
                  .map((manager) => ({
                    value: manager.id,
                    label: `${manager.firstName} ${manager.lastName}`,
                    originalData: manager,
                  }))}
                withFormik
                isExternalUserList
                setInvitedUsers={() => {}}
              />
            ) : (
              <div className={classes.EmptyMessage}>
                {t(
                  'dashboardComponents.Companies.CompanyDetails.Settings.ManagersAccess.emptyMessage',
                  'No managers with access'
                )}
              </div>
            )}
          </div>
          <div className={classes.Card} style={{ display: 'none' }}>
            <div className={classes.SectionTitle}>
              {t('company.settings.notification.title', 'Notification')}
            </div>
            <div className={classes.Table}>
              <div className={classes.HeaderRow}>
                <div className={classes.ColEvent}>
                  {t('company.settings.notification.headers.event', 'Event')}
                </div>
                <div className={classes.Col}>
                  {t('company.settings.notification.headers.active', 'Active')}
                </div>
                <div className={classes.Col}>
                  {t('company.settings.notification.headers.email', 'Email')}
                </div>
                <div className={classes.Col}>
                  {t('company.settings.notification.headers.inApp', 'In-App')}
                </div>
              </div>
              <div className={classes.Divider} />
              {notificationSettings.map((row) => (
                <div className={classes.Row} key={row.key}>
                  <div className={classes.ColEvent}>
                    {t(`company.settings.notification.events.${row.key}`)}
                  </div>
                  <div className={classes.Col}>
                    <Switch
                      isEnabled={row.active}
                      onClick={() => toggleNotification(row.key, 'active')}
                    />
                  </div>
                  <div className={classes.Col}>
                    <Checkbox
                      isSelected={row.email}
                      onChange={() => toggleNotification(row.key, 'email')}
                    />
                  </div>
                  <div className={classes.Col}>
                    <Checkbox
                      isSelected={row.inApp}
                      onChange={() => toggleNotification(row.key, 'inApp')}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
